import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import { useAuth } from '../../context/AuthContext';
import { ReportsHistory, useReports } from './ReportsRail';
import styles from './Analytics.module.css';

const FORMAT_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'csv', label: 'CSV' },
  { value: 'pdf', label: 'PDF' },
];

const CHANNEL_OPTIONS = [
  { value: 'ALL', label: 'All channels' },
  { value: 'POS', label: 'POS' },
  { value: 'DELIVERY', label: 'Delivery' },
];

function jobCreatedYmd(job) {
  if (!job?.created_at) return '';
  try {
    return new Date(job.created_at).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  } catch {
    return String(job.created_at).slice(0, 10);
  }
}

export default function ReportsHistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.groups?.includes('Admin');
  const reports = useReports(isAdmin);

  const [type, setType] = useState('ALL');
  const [format, setFormat] = useState('ALL');
  const [channel, setChannel] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filteredJobs = useMemo(() => {
    return reports.jobs.filter((job) => {
      if (type !== 'ALL' && job.type !== type) return false;
      if (format !== 'ALL' && String(job.format || '').toLowerCase() !== format) return false;
      if (channel !== 'ALL') {
        const jobChannel = String(job.channel || 'ALL').toUpperCase();
        if (jobChannel !== channel) return false;
      }
      const created = jobCreatedYmd(job);
      if (dateFrom && created && created < dateFrom) return false;
      if (dateTo && created && created > dateTo) return false;
      if ((dateFrom || dateTo) && !created) return false;
      return true;
    });
  }, [reports.jobs, type, format, channel, dateFrom, dateTo]);

  const filteredReports = useMemo(
    () => ({ ...reports, jobs: filteredJobs }),
    [reports, filteredJobs],
  );

  const hasActiveFilters = type !== 'ALL' || format !== 'ALL' || channel !== 'ALL' || dateFrom || dateTo;
  const clearFilters = () => {
    setType('ALL');
    setFormat('ALL');
    setChannel('ALL');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Analytics"
        title="Previous reports"
        actions={(
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/analytics')}>
            Back to analytics
          </Button>
        )}
      />
      <p className={styles.historyIntro}>
        Up to 20 most recent report jobs. Filter by date generated, type, format, or channel.
      </p>

      <div className={styles.controls}>
        <div className={styles.dateFilters}>
          <label className={styles.dateField}>
            Generated from
            <input
              type="date"
              className={styles.dateInput}
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className={styles.dateField}>
            Generated to
            <input
              type="date"
              className={styles.dateInput}
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
        </div>

        <div className={styles.controlBlock}>
          <span className={styles.controlLabel}>Type</span>
          <select
            className={styles.reportSelect}
            value={type}
            onChange={(e) => setType(e.target.value)}
            aria-label="Report type"
          >
            <option value="ALL">All types</option>
            {reports.templates.map((t) => (
              <option key={t.type} value={t.type}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className={styles.controlBlock}>
          <span className={styles.controlLabel}>Format</span>
          <div className={styles.segments} role="group" aria-label="Format filter">
            {FORMAT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.segment} ${format === opt.value ? styles.segmentActive : ''}`}
                onClick={() => setFormat(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.controlBlock}>
          <span className={styles.controlLabel}>Channel</span>
          <select
            className={styles.reportSelect}
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            aria-label="Channel filter"
          >
            {CHANNEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {hasActiveFilters && (
          <div className={styles.controlBlock}>
            <span className={styles.controlLabel} aria-hidden="true">&nbsp;</span>
            <button type="button" className={styles.clearFiltersBtn} onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}
      </div>

      <p className={styles.filterSummary}>
        Showing {filteredJobs.length} of {reports.jobs.length} report
        {reports.jobs.length === 1 ? '' : 's'}
      </p>

      <ReportsHistory
        reports={filteredReports}
        mode="full"
        emptyMessage={
          hasActiveFilters
            ? 'No reports match these filters.'
            : 'No previous reports yet.'
        }
      />
    </div>
  );
}
